import { Amplify } from 'aws-amplify';
import {
  signInWithRedirect,
  signOut as amplifySignOut,
  getCurrentUser,
  fetchAuthSession,
} from 'aws-amplify/auth';
import config from './config';

/** Cognito group on di-mcp-users granting portal admin rights. */
export const ADMIN_GROUP = 'admins';

/**
 * Configure AWS Amplify with Cognito settings.
 * Must be called once before any auth operations.
 */
export function configureAmplify(): void {
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: config.userPoolId,
        userPoolClientId: config.userPoolClientId,
        loginWith: {
          oauth: {
            domain: config.cognitoDomain,
            // aws.cognito.signin.user.admin is REQUIRED for the self-service MFA
            // APIs (setUpTOTP/verifyTOTPSetup/updateMFAPreference). Without it the
            // hosted-UI access token is rejected with "Access Token does not have
            // required scopes" during MFA enrollment (DAP-18). The scope must also
            // be present in the app client's AllowedOAuthScopes.
            scopes: ['openid', 'email', 'profile', 'aws.cognito.signin.user.admin'],
            redirectSignIn: [config.redirectUri],
            redirectSignOut: [config.redirectUri],
            responseType: 'code',
          },
        },
      },
    },
  });
}

/**
 * Initiate sign-in via the Cognito hosted UI. No provider is forced — the
 * hosted UI offers Google AND email+password (Phase 1: one pool, two methods).
 */
export async function signIn(): Promise<void> {
  await signInWithRedirect();
}

/**
 * Force a fresh hosted-UI authorization so the new access token carries the
 * current OAuth scopes. Cognito's refresh-token grant does NOT widen an
 * already-issued authorization, so a session that predates a scope change keeps
 * failing scope-gated calls (e.g. MFA self-service APIs) until it fully
 * re-authenticates. Used to recover users whose cached token lacks
 * aws.cognito.signin.user.admin (DAP-18).
 */
export async function reauthenticate(): Promise<void> {
  try {
    // If the Cognito session cookie is still valid this re-authorizes silently
    // and mints a token with the widened scopes.
    await signInWithRedirect();
  } catch {
    // Amplify refuses signInWithRedirect while a user is signed in — clear the
    // session and let the normal login flow re-authorize with the wider scope.
    await signOut();
  }
}

/**
 * Sign the current user out and redirect to the Cognito logout endpoint.
 */
export async function signOut(): Promise<void> {
  try {
    await amplifySignOut({ global: true });
  } catch {
    // Fallback: redirect to Cognito logout
    const { cognitoDomain, userPoolClientId, redirectUri } = config;
    const params = new URLSearchParams({
      client_id: userPoolClientId,
      logout_uri: redirectUri,
    });
    window.location.href = `https://${cognitoDomain}/logout?${params.toString()}`;
  }
}

export interface SessionInfo {
  username: string;
  name: string;
  email: string;
  idToken: string;
  accessToken: string;
  groups: string[];
}

/**
 * Retrieve the current authenticated user session, including tokens and group
 * membership.  Returns null if the user is not authenticated.
 */
export async function getSession(): Promise<SessionInfo | null> {
  try {
    const user = await getCurrentUser();
    const session = await fetchAuthSession();

    const idToken = session.tokens?.idToken;
    const accessToken = session.tokens?.accessToken;

    if (!idToken || !accessToken) {
      return null;
    }

    const payload = idToken.payload ?? {};
    const groups: string[] = (payload['cognito:groups'] as string[] | undefined) ?? [];
    const email = (payload['email'] as string) ?? '';
    const name = (payload['name'] as string) ?? '';

    return {
      username: user.username,
      name,
      email,
      idToken: idToken.toString(),
      accessToken: accessToken.toString(),
      groups,
    };
  } catch {
    return null;
  }
}

/**
 * Check whether the currently authenticated user belongs to the admins group.
 */
export async function isAdmin(): Promise<boolean> {
  const session = await getSession();
  if (!session) return false;
  return session.groups.includes(ADMIN_GROUP);
}
